import { isEmpty, get, omit, forEach, trim, toLower, findLastIndex } from 'lodash';
import {
    getScreenData,
    setScreenData,
    removeScreenData,
    Store,
    CwLog,
    setErrorScreenMessage,
    formatMessage,
    ACTIVE_SCREEN_KEY,
    showDialog,
    getUserId,
} from 'cw-ui-core';
import { toMap, checkIfUserHaveGivenPermissions } from 'src/util/Util';
import {
    cleanupSelectedPermissions,
    getUpdatedRolePermissions,
    GenerateUpdates,
} from 'src/features/application-roles/components/GranularPremission/utils';
import {
    fetchRoleDetails,
    editRoleService,
    cloneRoleService,
    detectDuplicateRolesService,
    fetchUsersForAppRole,
} from 'src/services/ApplicationRoleManagementService';
import {
    EDIT_APP_ROLE_DRAWER,
    CLONE_APP_ROLE_DRAWER,
    CUSTOM_ROLE_TYPE,
    DUPLICATE_APP_ROLES_DIALOG,
    DEFAULT_ROLE_TYPE,
} from 'src/features/application-roles/constants';
import Locale from 'src/i18n';
import {
    SELECTED_STATE,
    DESELECTED_STATE,
} from 'src/features/application-roles/components/GranularPremission/constants';
import { ALERT_TYPE } from 'src/asio-neon-components/Constants';
import setAlertScreenMessage from 'src/asio-neon-components/Utils';
import { ASOI_PLATFORM_APP_ID, PERMISSIONS } from 'src/constants';
import { appRolesApi } from './services/appRolesService';

export const setDrawerDomainsData = (clickedRoleClientId, activeClientId, screenKey) => {
    // we have just asio app now, so just copy already loaded domains from active screen to drawer
    const domains = toMap(getScreenData(Store().getState().screen, ['domains'], ACTIVE_SCREEN_KEY));
    if (!isEmpty(domains)) {
        Store().dispatch(setScreenData(['domains'], domains, screenKey));
        Store().dispatch(setScreenData(['permissionsLoading'], false, screenKey));
    }
};

export const setDrawerPermissionsData = (activeRoleId, clickedRoleId, clientId, screenKey) => {
    if (activeRoleId !== clickedRoleId) {
        cleanupSelectedPermissions(screenKey);

        // fetch selected permissions for the role if selected role doesn't match with the clicked on
        Store().runSaga(fetchRoleDetails, clientId, clickedRoleId, screenKey);
    } else {
        const selectedRolePermissions = toMap(
            getScreenData(Store().getState().screen, ['activeRole', 'selectedPermissions'])
        );

        // reuse selected permissions from selected role, if it matches with the clicked on
        Store().dispatch(setScreenData(['activeRole', 'selectedPermissions'], selectedRolePermissions, screenKey));
    }
};

export const onEditApplicationRoleUtil = async (clientId, roleDetails, selectedPermissions) => {
    const currentRoleId = get(roleDetails, 'id');
    const requestedBy = getUserId(Store().getState().oidc);
    const result = await Store()
        .runSaga(
            editRoleService,
            clientId,
            omit(roleDetails, ['id', 'name', 'type']),
            selectedPermissions,
            currentRoleId,
            requestedBy,
            EDIT_APP_ROLE_DRAWER
        )
        .toPromise();

    if (!result.isError) {
        const domainsUpdated = toMap(getScreenData(Store().getState().screen, ['domains'], EDIT_APP_ROLE_DRAWER));
        Store().dispatch(setScreenData(['domains'], domainsUpdated));
        await Store().dispatch(
            appRolesApi.endpoints.getAppRolesList.initiate(
                {
                    appId: clientId,
                },
                { forceRefetch: true }
            )
        );

        Store().dispatch(setScreenData(['activeRole'], roleDetails));
        Store().dispatch(setScreenData(['activeClientId'], clientId));
        await Store().runSaga(fetchRoleDetails, clientId, roleDetails?.id).toPromise();
        await Store().runSaga(fetchUsersForAppRole, clientId, roleDetails?.id).toPromise();
    }
};

export const onEditApplicationRole = async () => {
    // get role details
    const roleDetails = toMap(getScreenData(Store().getState().screen, ['activeRole'], EDIT_APP_ROLE_DRAWER));

    // get client id
    const clientId = getScreenData(Store().getState().screen, ['activeClientId'], EDIT_APP_ROLE_DRAWER);

    roleDetails.displayName = roleDetails.name;

    // prepare permission set
    roleDetails.legacyRoleIdRef = get(roleDetails, ['selectedPermissions', 'legacyRoleId']);
    const selectedPermissions = getUpdatedRolePermissions(EDIT_APP_ROLE_DRAWER);

    // we should not allow cloning or editing application role with 0 permissions
    if (isEmpty(selectedPermissions.legacyPermissions) && isEmpty(selectedPermissions.platformPermissions)) {
        Store().dispatch(
            setAlertScreenMessage(
                ALERT_TYPE.error,
                '',
                formatMessage(Locale.errorAtLeastOnePermissionRequired, {
                    oparation: formatMessage(Locale.updateApplicationRole),
                }),
                true,
                EDIT_APP_ROLE_DRAWER
            )
        );

        return;
    }

    // data cleanup
    delete roleDetails.selectedPermissions;
    delete roleDetails.readOnly;
    delete roleDetails.isAdminRole;
    delete roleDetails.legacyRoleIdRef;
    delete roleDetails.assignedUsersCount;
    clearPermissionsSearchQuery();

    // call service and return error object
    try {
        // call duplicate roles service and return error object
        const { duplicateRoles, isError } = await Store()
            .runSaga(detectDuplicateRolesService, clientId, selectedPermissions, roleDetails.id)
            .toPromise();

        //  if empty response or error
        if (isError || isEmpty(duplicateRoles)) {
            await onEditApplicationRoleUtil(clientId, roleDetails, selectedPermissions);
            return;
        }

        //  if it returns list of roles then show dialog and pass necessary info into dialog
        Store().dispatch(
            showDialog(DUPLICATE_APP_ROLES_DIALOG, {
                roleDetails,
                clientId,
                selectedPermissions,
                isEditOperation: true,
                duplicateRoles,
            })
        );
    } catch (e) {
        CwLog.error(e?.message);
    }
};

export const sortApplicationRoles = (roles = []) => {
    // get last default roles out from roles array
    const lastIndexOfDefaultRole = findLastIndex(roles, role => role.type === DEFAULT_ROLE_TYPE);
    const sortedRoles = [];

    // scan all the default roles and find out admin roles
    // insert them first into sortedArray
    for (let i = 0; i < lastIndexOfDefaultRole; i += 1) {
        if (roles[i].isAdminRole) sortedRoles.push(roles[i]);
    }

    // exclude all the admin roles and insert them into sorted array.
    for (let i = 0; i < roles.length; i += 1) {
        if (!roles[i].isAdminRole) {
            sortedRoles.push(roles[i]);
        }
    }

    return sortedRoles;
};

export const onCloneApplicationRoleUtil = async (
    clientId,
    roleDetails,
    selectedPermissions,
    cloneContentsAssignmentAsWell
) => {
    const requestedBy = getUserId(Store().getState().oidc);
    const result = await Store()
        .runSaga(
            cloneRoleService,
            clientId,
            roleDetails,
            selectedPermissions,
            requestedBy,
            CLONE_APP_ROLE_DRAWER,
            cloneContentsAssignmentAsWell
        )
        .toPromise();

    if (!result.isError) {
        Store().dispatch(setScreenData(['isRoleCloned'], true, CLONE_APP_ROLE_DRAWER));

        let updatedRolesList;
        const { data, isError, error } = await Store().dispatch(
            appRolesApi.endpoints.getAppRolesList.initiate(
                {
                    appId: ASOI_PLATFORM_APP_ID,
                },
                { forceRefetch: true }
            )
        );
        updatedRolesList = data ? sortApplicationRoles(data) : [];
        if (isError) {
            const { message } = error.data;
            Store().dispatch(setErrorScreenMessage(message, false, [], ACTIVE_SCREEN_KEY));
        }

        const updatedDomains = toMap(getScreenData(Store().getState().screen, ['domains'], CLONE_APP_ROLE_DRAWER));
        const clonedRoleDetails = updatedRolesList.find(({ name }) => name === roleDetails.displayName);

        if (!isEmpty(clonedRoleDetails)) {
            Store().dispatch(setScreenData(['domains'], updatedDomains, ACTIVE_SCREEN_KEY));
            Store().dispatch(setScreenData(['activeRole'], clonedRoleDetails, ACTIVE_SCREEN_KEY));
            Store().dispatch(setScreenData(['activeClientId'], clientId, ACTIVE_SCREEN_KEY));

            await Store().runSaga(fetchRoleDetails, clientId, clonedRoleDetails.id).toPromise();
            await Store().runSaga(fetchUsersForAppRole, clientId, clonedRoleDetails.id).toPromise();
        }
    }
};

export const onCloneApplicationRole = async () => {
    // get role details
    const roleDetails = toMap(getScreenData(Store().getState().screen, ['activeRole'], CLONE_APP_ROLE_DRAWER));

    // get client id
    const clientId = getScreenData(Store().getState().screen, ['activeClientId'], CLONE_APP_ROLE_DRAWER);

    // should clone content assignment as well
    const cloneContentsAssignmentAsWell = getScreenData(
        Store().getState().screen,
        ['cloneContentsAssignmentAsWell'],
        CLONE_APP_ROLE_DRAWER
    );

    // prepare permission set
    roleDetails.legacyRoleIdRef = get(roleDetails, ['selectedPermissions', 'legacyRoleId']);
    const selectedPermissions = getUpdatedRolePermissions(CLONE_APP_ROLE_DRAWER);

    // we should not allow cloning or editing application role with 0 permissions
    if (isEmpty(selectedPermissions.legacyPermissions) && isEmpty(selectedPermissions.platformPermissions)) {
        Store().dispatch(
            setErrorScreenMessage(
                formatMessage(Locale.errorAtLeastOnePermissionRequired, {
                    oparation: formatMessage(Locale.cloneApplicationRole),
                }),
                false,
                [],
                CLONE_APP_ROLE_DRAWER
            )
        );

        Store().dispatch(
            setAlertScreenMessage(
                ALERT_TYPE.error,
                '',
                formatMessage(Locale.errorAtLeastOnePermissionRequired, {
                    oparation: formatMessage(Locale.cloneApplicationRole),
                }),
                true,
                CLONE_APP_ROLE_DRAWER
            )
        );
        return;
    }

    roleDetails.displayName = roleDetails.name;
    roleDetails.roleType = CUSTOM_ROLE_TYPE;
    roleDetails.clonedFromId = roleDetails.id;

    // data cleanup
    delete roleDetails.selectedPermissions;
    delete roleDetails.id;
    delete roleDetails.name;
    delete roleDetails.readOnly;
    delete roleDetails.isAdminRole;
    delete roleDetails.type;

    // call service and return error object
    try {
        // call duplicate roles service and return error object
        const { duplicateRoles, isError } = await Store()
            .runSaga(detectDuplicateRolesService, clientId, selectedPermissions)
            .toPromise();

        //  if empty response or error
        if (isError || isEmpty(duplicateRoles)) {
            await onCloneApplicationRoleUtil(clientId, roleDetails, selectedPermissions, cloneContentsAssignmentAsWell);
            return;
        }

        //  if it returns list of roles then show dialog and pass necessary info into dialog
        Store().dispatch(
            showDialog(DUPLICATE_APP_ROLES_DIALOG, {
                roleDetails,
                clientId,
                selectedPermissions,
                duplicateRoles,
                cloneContentsAssignmentAsWell,
            })
        );
    } catch (e) {
        CwLog.error(e?.message);
    }
};

export const checkAllPermissions = (domainId, isChecked, screenKey = ACTIVE_SCREEN_KEY) => {
    const updatedPermissions = {};
    const selectedPermissionsMap = toMap(
        getScreenData(Store().getState().screen, ['domains', domainId, 'selectedPermissions'], screenKey)
    );
    const selectedPermissionKeys = selectedPermissionsMap ? Object.keys(selectedPermissionsMap) : [];
    forEach(selectedPermissionKeys, permId => {
        updatedPermissions[permId] = {
            ...get(selectedPermissionsMap, [permId], {}),
            value: isChecked ? SELECTED_STATE : DESELECTED_STATE,
        };
    });

    Store().dispatch(setScreenData(['domains', domainId, 'selectedPermissions'], updatedPermissions, screenKey));
    GenerateUpdates(screenKey);
};

export const searchMatch = (i, field, text) => {
    return i[field] && i[field].toLocaleLowerCase().indexOf(text.toLocaleLowerCase()) > -1;
};

export const buildAllowedOperationsForRole = (
    role,
    clientId,
    onCloneRole,
    onEditRole,
    onDeleteRole,
    optionsType = 'options' // 'options' or 'actions'
) => {
    const isCustomRole = role?.type?.toLowerCase() === CUSTOM_ROLE_TYPE;
    const isAdmin = role?.isAdminRole;
    const isReadOnly = role?.readOnly;
    const result = [];

    if (isAdmin || !checkIfUserHaveGivenPermissions([PERMISSIONS.APPLICATION_ROLES_UPDATE])) {
        return result;
    }

    const addItem = (id, label, handler) => {
        if (optionsType === 'actions') {
            result.push({
                id,
                title: label,
                onClick: handler,
            });
        } else {
            result.push({
                label,
                action: handler,
            });
        }
    };

    addItem('clone', formatMessage(Locale.clone), () => onCloneRole(role, clientId));
    if (!isReadOnly) {
        addItem('edit', formatMessage(Locale.edit), () => onEditRole(role, clientId));
    }
    if (isCustomRole) {
        addItem('delete', formatMessage(Locale.delete), () => onDeleteRole(role, clientId));
    }

    return result;
};

export const clearPermissionsSearchQuery = () => {
    Store().dispatch(setScreenData(['permissionsSearchQuery'], '', ACTIVE_SCREEN_KEY));
    Store().dispatch(removeScreenData('filteredDomains'));
};

export const filterDomainsAndPermissions = (item, query) => {
    let searchQuery = trim(query || '');
    searchQuery = toLower(searchQuery);
    const displayName = toLower(item?.displayName || '');

    const searchInPermissions = (permissions, query) => {
        return permissions?.some(permission => {
            const foundInDisplayName = toLower(permission?.displayName || '').includes(query);

            const foundInNestedPermissions = searchInPermissions(permission?.children || [], query);

            return foundInDisplayName || foundInNestedPermissions;
        });
    };

    const foundMatchingPermissions = searchInPermissions(item?.permissions, searchQuery);

    return displayName.includes(searchQuery) || foundMatchingPermissions;
};
